import { CssBaseline } from '@mui/material';
import React, { useEffect, useRef } from 'react';
import {
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  data,
  useRouteLoaderData,
  type LoaderFunctionArgs,
  type MetaFunction,
} from 'react-router';
import { AppHeader } from '~/components/AppHeader';
import useAttributionDetails from '~/hooks/useAttributionDetails';
import useIsNavigating from '~/hooks/useIsNavigating';
import useRoute from '~/hooks/useRoute';
import useRouter from '~/hooks/useRouter';
import getInitialData, { type InitialData } from '~/lib/getInitialData';
import { rootDataContext } from '~/lib/rootDataContext';
import requestLogger from '~/middleware/requestLogger';
import rootData from '~/middleware/rootData';
import MuiProvider from '~/mui/MuiProvider';
import { RootDataProvider } from '~/providers/RootDataProvider';
import { AUTHORIZED_FRONTEND_COOKIE_NAME } from '~/scripts/constants';
import { getCookie } from '~/scripts/cookie';
import csp from '~/scripts/csp';
import isFullUser from '~/scripts/isFullUser';
import isInjectedScriptError from '~/scripts/isInjectedScriptError';
import { logger } from '~/scripts/logger';
import useDataLayer from '~/services/datalayer';
import type { Route } from './+types/root';
import stylesheet from './app.css?url';
import { PostHogProvider, usePostHog } from '@posthog/react';

export { default as ErrorBoundary } from '~/components/ErrorBoundary';

function buildCsp(cookieHeader: string | null = null) {
  return Object.entries(
    csp(import.meta.env.VITE_BUILD_VERSION || '', cookieHeader)
  )
    .map(([key, values]) => `${key} ${values.join(' ')}`)
    .join('; ');
}

export const headers = ({ loaderHeaders }: Route.HeadersArgs) => {
  return {
    'X-Frame-Options': 'deny',
    'X-Content-Type-Options': 'nosniff',
    'Accept-Encoding': 'gzip, deflate, br',
    'Access-Control-Allow-Origin': import.meta.env.VITE_ORIGIN,
    'Content-Security-Policy':
      loaderHeaders.get('Content-Security-Policy') || buildCsp(),
  };
};

export const middleware: Route.MiddlewareFunction[] = [requestLogger, rootData];

export const clientMiddleware: Route.ClientMiddlewareFunction[] = [
  async ({ request, context }, next) => {
    if (window.rootData) {
      context.set(rootDataContext, window.rootData);
    } else {
      const data = await getInitialData(request);
      context.set(rootDataContext, data);
    }
    return next();
  },
];

export const loader = async ({ context, request }: LoaderFunctionArgs) => {
  // per-request CSP so posthog violation reports carry session/distinct ids
  return data(context.get(rootDataContext)!, {
    headers: {
      'Content-Security-Policy': buildCsp(request.headers.get('Cookie')),
    },
  });
};

export const meta: MetaFunction<typeof loader> = () => {
  return [
    { title: 'NerdyData.com', description: 'NerdyData.com' },
    { 'twitter:site': '@nerdydata' },
    { 'twitter:card': 'summary' },
    { build: import.meta.env.VITE_BUILD_VERSION || '' },
    { commit: import.meta.env.VITE_GIT_COMMIT || '' },
    { env: import.meta.env.PROD ? 'production' : 'development' },
  ];
};
export const links: Route.LinksFunction = () => [
  {
    rel: 'preconnect',
    href: import.meta.env.VITE_STATIC_ASSETS_URL,
  },
  {
    rel: 'preconnect',
    href: import.meta.env.VITE_API_URL,
  },
  {
    rel: 'stylesheet',
    href: stylesheet,
  },
  // {
  //   rel: 'preload',
  //   crossOrigin: 'anonymous',
  //   as: 'font',
  //   href: `${
  //     import.meta.env.VITE_STATIC_ASSETS_URL
  //   }/font/MaterialIcons-Regular.ttf`,
  // },
  // {
  //   rel: 'preload',
  //   crossOrigin: 'anonymous',
  //   as: 'font',
  //   href: `${
  //     import.meta.env.VITE_STATIC_ASSETS_URL
  //   }/font/noto-sans-v27-latin-regular.woff2`,
  // },
];

export function Layout({ children }: { children: React.ReactNode }) {
  const currentRootData: InitialData =
    useRouteLoaderData<typeof loader>('root')!;
  const posthog = usePostHog();
  const isNavigating = useIsNavigating();
  const route = useRoute();
  const dataLayer = useDataLayer();
  const router = useRouter();
  const urlRef = useRef('');
  useAttributionDetails();

  // Global error handlers for uncaught exceptions
  useEffect(() => {
    const handleUnhandledError = (event: ErrorEvent) => {
      if (isInjectedScriptError(event)) {
        return;
      }
      logger.error('[UNCAUGHT EXCEPTION]', event.error);
      posthog.captureException(event.error || new Error(event.message));
    };

    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      logger.error('[UNHANDLED PROMISE REJECTION]', event.reason);
      const error =
        event.reason instanceof Error
          ? event.reason
          : new Error(String(event.reason));
      posthog.captureException(error);
    };

    window.addEventListener('error', handleUnhandledError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    return () => {
      window.removeEventListener('error', handleUnhandledError);
      window.removeEventListener(
        'unhandledrejection',
        handleUnhandledRejection
      );
    };
  }, [currentRootData?.user?.uuid, dataLayer, posthog]);

  // init once; root loader revalidation gives a new `user` ref each navigation
  useEffect(() => {
    if (posthog.__loaded) {
      return;
    }
    posthog.init(import.meta.env.VITE_POSTHOG_CLIENT_TOKEN, {
      api_host: 'https://ph.nerdydata.com',
      session_recording: {
        maskAllInputs: false,
        maskInputOptions: {
          password: true,
        },
      },
      // disable_conversations: true,
      //api_host: 'https://us.i.posthog.com',
    });
    // super properties sent with every event (incl. $exception), same
    // names as the API's register() so errors can be filtered by service
    posthog.register({
      service: 'www.nerdydata.com',
      commit: import.meta.env.VITE_GIT_COMMIT || '',
      app_version: import.meta.env.VITE_BUILD_VERSION || '',
    });
    window.posthog = posthog;
  }, [posthog]);

  useEffect(() => {
    if (isFullUser(currentRootData?.user)) {
      dataLayer.push({
        event: 'identify',
        user: currentRootData?.user,
      });
      posthog.identify(currentRootData?.user?.uuid, {
        email: currentRootData?.user?.email,
        name: currentRootData?.user?.name,
        plan: currentRootData?.user?.account?.plans_id,
        commit: import.meta.env.VITE_GIT_COMMIT,
        app_version: import.meta.env.VITE_BUILD_VERSION,
      });
      posthog.setIdentity(
        currentRootData?.user?.uuid,
        currentRootData?.posthogIdentityHash || ''
      );
    } else if (
      currentRootData?.user &&
      // getInitialData falls back to a guest when /app/users/me fails, so
      // only treat a missing auth cookie as a real logout
      !getCookie(AUTHORIZED_FRONTEND_COOKIE_NAME) &&
      posthog.get_property('$user_state') === 'identified'
    ) {
      // logged out without the /logout page rendering (SSR /logout redirect,
      // expired session), so drop the previous user's distinct id
      posthog.reset();
    }
  }, [
    currentRootData?.user,
    dataLayer,
    currentRootData?.posthogIdentityHash,
    posthog,
  ]);

  useEffect(() => {
    if (urlRef.current !== `${route.path}?${JSON.stringify(route.query)}`) {
      urlRef.current = `${route.path}?${JSON.stringify(route.query)}`;
      dataLayer.push({
        event: '$viewContentLoaded',
        user: currentRootData?.user,
      });
    }
  }, [route.path, route.query, currentRootData?.user, dataLayer]);

  const featuresRef = useRef(currentRootData?.features || {});
  useEffect(() => {
    if (
      JSON.stringify(featuresRef.current) !==
      JSON.stringify(currentRootData?.features)
    ) {
      featuresRef.current = currentRootData?.features || {};
      dataLayer.push({
        event: 'features.loaded',
        features: currentRootData?.features,
      });
    }

    if (
      isFullUser(currentRootData?.user) &&
      (!currentRootData?.user.firstname ||
        !currentRootData?.user.lastname ||
        !currentRootData?.user.department ||
        !currentRootData.user.company) &&
      route.path !== '/onboarding'
    ) {
      router.push({
        path: '/onboarding',
        query: route.query,
      });
    }
  }, [currentRootData, route.path, router, dataLayer, route.query]);

  return (
    <RootDataProvider initialData={currentRootData}>
      <html
        lang="en"
        data-web-commit={import.meta.env.VITE_GIT_COMMIT || ''}
        data-web-version={import.meta.env.VITE_BUILD_VERSION || ''}
        data-api-commit={currentRootData?.apiCommit || ''}
        data-api-version={currentRootData?.apiVersion || ''}
        data-assets={import.meta.env.VITE_STATIC_ASSETS_URL || ''}
        data-user-uuid={currentRootData?.user?.uuid || ''}>
        <head>
          <meta charSet="utf-8" />
          <meta httpEquiv="X-UA-Compatible" content="IE=edge" />
          <meta name="viewport" content="width=device-width, initial-scale=1" />
          <link rel="icon" type="image/x-icon" href="/favicon.ico" />
          <link
            rel="icon"
            type="image/png"
            sizes="32x32"
            href={`${import.meta.env.VITE_STATIC_ASSETS_URL}/assets/img/logo/favicon-32.png`}
          />
          <link
            rel="icon"
            type="image/png"
            sizes="16x16"
            href={`${import.meta.env.VITE_STATIC_ASSETS_URL}/assets/img/logo/favicon-16.png`}
          />
          <link rel="apple-touch-icon" href="/assets/img/logo/logo-180.png" />
          <link rel="manifest" href="/manifest.json" />
          <meta name="slack-app-id" content="AV4BZEDKK" />
          <meta
            name="google-site-verification"
            content="IUWWBYntaaASW7LveYpuBsw-jfx46SIzyLm2LXRtvFU"
          />
          <meta name="mobile-web-app-capable" content="yes" />
          <meta name="apple-mobile-web-app-capable" content="yes" />
          <meta name="theme-color" content="#1e88e5" />
          <link rel="preconnect" href="https://m.stripe.com" />
          <link rel="preconnect" href="https://storage.googleapis.com" />
          <Meta />
          <Links />

          <script
            dangerouslySetInnerHTML={{
              __html: `
              window.dataLayer = window.dataLayer || [];
              window.dataLayer.push({
                'gtm.start': new Date().getTime(),
                event: 'gtm.js',
              });
              `,
            }}
          />
          <script
            dangerouslySetInnerHTML={{
              __html: `
              window.features = ${JSON.stringify(currentRootData?.features || {})}
              `,
            }}
          />
          <script
            id="schema-json"
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: `{
          "@context": "http://schema.org",
          "@type": "WebSite",
          "url": "https://www.nerdydata.com/",
          "name": "NerdyData.com Search",
          "potentialAction": {
            "@type": "SearchAction",
            "target": {
              "@type": "EntryPoint",
              "urlTemplate": "https://www.nerdydata.com/search?query={search_term_string}"
            },
            "query-input": "required name=search_term_string"
          },
          "query-input": "required name=search_term_string"
        }`,
            }}
          />
          <script
            id="schema-organization"
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: `{
          "@context": "https://schema.org",
          "@type": "Organization",
          "name": "NerdyData",
          "url": "https://www.nerdydata.com",
          "logo": "https://www.nerdydata.com/assets/img/logo_square.png",
          "description": "NerdyData is a search engine for the web's source code. Find websites that use specific technologies, code snippets, or HTML patterns.",
          "foundingDate": "2013",
          "sameAs": [
            "https://twitter.com/nerdydata"
          ]
        }`,
            }}
          />
          <script
            dangerouslySetInnerHTML={{
              __html: `
              window.siteConfig = {};
              window.api = {};
              try {window.siteConfig.commit = ${JSON.stringify(
                import.meta.env.VITE_GIT_COMMIT
              )} || '?';} catch(e) {console.warn(e)}
              try {window.siteConfig.version = ${JSON.stringify(
                import.meta.env.VITE_BUILD_VERSION
              )} || '?';} catch(e) {console.warn(e)}
              try {window.siteConfig.recaptcha = ${JSON.stringify(
                import.meta.env.VITE_RECAPTCHA_KEY
              )};} catch(e) {console.warn(e)}
              try {window.siteConfig.staticAssetsUrl = ${JSON.stringify(
                import.meta.env.VITE_STATIC_ASSETS_URL
              )} || '';} catch(e) {console.warn(e)}
              try {window.api.url = ${JSON.stringify(
                import.meta.env.VITE_API_URL
              )} || '//api.nerdydata.com';} catch(e) {console.warn(e)}
        `,
            }}
          />
          <script src="https://tm.nerdydata.com/" defer async />
          <script
            src={`https://www.google.com/recaptcha/api.js?render=${
              import.meta.env.VITE_RECAPTCHA_KEY
            }`}
            defer
            async
          />
        </head>
        <body>
          <PostHogProvider client={posthog}>
            <MuiProvider>
              <CssBaseline />
              <AppHeader loading={isNavigating} />
              {children}
            </MuiProvider>
          </PostHogProvider>
          <ScrollRestoration />
          <Scripts />
          <noscript>
            <iframe
              title="Google Tag Manager"
              src="https://www.googletagmanager.com/ns.html?id=GTM-KH3PCL"
              height="0"
              width="0"
              style={{ display: 'none', visibility: 'hidden' }}></iframe>
          </noscript>
        </body>
      </html>
    </RootDataProvider>
  );
}

export default function App() {
  return <Outlet />;
}
